import { Chip, Flex, Icon, Icons, Paragraph } from "@ir/ui";
import type {
  UserDetailsBrand,
  UserDetailsFeature,
} from "@src/domain/entities/auth/user/UserDetails";
import type {
  FeatureLookupItem,
  FeaturePermission,
} from "@src/domain/dto/features";
import type { RolePermissionItem } from "@src/presentation/providers/RolesPermissionsProvider";

export interface RoleOption {
  label: string;
  value: string;
}

interface PermissionChipItem {
  id: number;
  name: string;
}

interface BrandFeatureItem {
  featureId: number;
  featureName: string;
  permissions: PermissionChipItem[];
}

interface BrandPermissionsContentProps {
  brand: UserDetailsBrand;
  rolePermissions: RolePermissionItem[] | null;
  featuresLookup: FeatureLookupItem[] | null;
  selectedRoleByBrandId: Record<number, number | null>;
  customPermissionIdsByBrandFeature: Record<
    number,
    Record<number, Set<number>>
  >;
  onToggleCustomPermission: (
    brandId: number,
    featureId: number,
    permissionId: number,
  ) => void;
}

function getActiveRolePermissionIds(
  permissions: FeaturePermission[] | undefined,
): Set<number> {
  const ids = new Set<number>();
  if (!permissions?.length) return ids;
  for (const permission of permissions) {
    if (permission.isActive) {
      ids.add(permission.id);
    }
  }
  return ids;
}

function toLookupFeatureItem(feature: FeatureLookupItem): BrandFeatureItem {
  const permissions: PermissionChipItem[] = [];
  for (const permission of feature.featurePermissions ?? []) {
    permissions.push({ id: permission.id, name: permission.name });
  }
  return {
    featureId: feature.featureId,
    featureName: feature.featureName,
    permissions,
  };
}

function toBrandFeatureItem(feature: UserDetailsFeature): BrandFeatureItem {
  const permissions: PermissionChipItem[] = [];
  for (const permission of feature.permissions ?? []) {
    permissions.push({
      id: permission.permissionId,
      name: permission.permissionName,
    });
  }
  return {
    featureId: feature.featureId,
    featureName: feature.featureName,
    permissions,
  };
}

function buildBrandFeatureItems(
  featuresLookup: FeatureLookupItem[] | null,
  brandFeatures: UserDetailsFeature[],
): BrandFeatureItem[] {
  const items: BrandFeatureItem[] = [];
  const brandFeatureIds = new Set(
    brandFeatures.map((feature) => feature.featureId),
  );
  const renderedFeatureIds = new Set<number>();

  for (const lookupFeature of featuresLookup ?? []) {
    if (!brandFeatureIds.has(lookupFeature.featureId)) continue;

    items.push(toLookupFeatureItem(lookupFeature));
    renderedFeatureIds.add(lookupFeature.featureId);
  }

  for (const brandFeature of brandFeatures) {
    if (!renderedFeatureIds.has(brandFeature.featureId)) {
      items.push(toBrandFeatureItem(brandFeature));
    }
  }

  return items;
}

function buildRolePermissionIdsByFeature(
  selectedBrandRole: RolePermissionItem | undefined,
): Map<number, Set<number>> {
  const rolePermissionIdsByFeature = new Map<number, Set<number>>();
  for (const feature of selectedBrandRole?.roleFeatures ?? []) {
    rolePermissionIdsByFeature.set(
      feature.featureId,
      getActiveRolePermissionIds(feature.featurePermissions),
    );
  }
  return rolePermissionIdsByFeature;
}

function getCustomPermissionIds(
  permissions: UserDetailsFeature["permissions"] | undefined,
): Set<number> {
  const ids = new Set<number>();
  if (!permissions?.length) return ids;
  for (const permission of permissions) {
    if (permission.isCustom) {
      ids.add(permission.permissionId);
    }
  }
  return ids;
}

function buildCustomPermissionIdsByFeature(
  brandFeatures: UserDetailsFeature[],
): Map<number, Set<number>> {
  const customPermissionIdsByFeature = new Map<number, Set<number>>();
  for (const feature of brandFeatures ?? []) {
    customPermissionIdsByFeature.set(
      feature.featureId,
      getCustomPermissionIds(feature.permissions),
    );
  }
  return customPermissionIdsByFeature;
}

export const UserDetailsBrandPermissionsContent = ({
  brand,
  rolePermissions,
  featuresLookup,
  selectedRoleByBrandId,
  customPermissionIdsByBrandFeature,
  onToggleCustomPermission,
}: BrandPermissionsContentProps) => {
  const selectedBrandRoleId =
    selectedRoleByBrandId[brand.brandId] ?? brand.roles?.[0]?.roleId ?? null;
  const selectedBrandRole = (rolePermissions ?? []).find(
    (role) => role.roleId === selectedBrandRoleId,
  );
  const rolePermissionIdsByFeature =
    buildRolePermissionIdsByFeature(selectedBrandRole);
  const customPermissionIdsByFeature = buildCustomPermissionIdsByFeature(
    brand.features ?? [],
  );
  const featureItems = buildBrandFeatureItems(
    featuresLookup,
    brand.features ?? [],
  );

  return (
    <Flex direction="column" gap={0.75} className="pt-2">
      {featureItems.map((feature) => {
        const rolePermissionIds =
          rolePermissionIdsByFeature.get(feature.featureId) ??
          new Set<number>();
        const customPermissionIds =
          customPermissionIdsByFeature.get(feature.featureId) ??
          new Set<number>();

        return (
          <Flex
            key={`${brand.brandId}-${feature.featureId}`}
            direction="column"
            gap={0.45}
          >
            <Paragraph className="m-0 text-grey-600 font-bold">
              {feature.featureName}
            </Paragraph>
            <Flex wrap="wrap" gap={0.4}>
              {feature.permissions.map((permission) => {
                const currentCustomIds =
                  customPermissionIdsByBrandFeature[brand.brandId]?.[
                    feature.featureId
                  ] ?? customPermissionIds;
                const isCustom = currentCustomIds.has(permission.id);
                const isFromRole = rolePermissionIds.has(permission.id);
                let chipColor: "warning" | "success" | "grey" = "grey";
                if (isCustom) {
                  chipColor = "warning";
                } else if (isFromRole) {
                  chipColor = "success";
                }

                return (
                  <Chip
                    key={`${brand.brandId}-${feature.featureId}-${permission.id}`}
                    label={permission.name}
                    size="small"
                    color={chipColor}
                    borderless
                    style={{
                      cursor: isFromRole ? "not-allowed" : "pointer",
                    }}
                    onClick={() => {
                      if (isFromRole) return;
                      onToggleCustomPermission(
                        brand.brandId,
                        feature.featureId,
                        permission.id,
                      );
                    }}
                    icon={
                      isCustom ? (
                        <Icon name={Icons.Check} size="small" />
                      ) : undefined
                    }
                  />
                );
              })}
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
};
